import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Phone } from "lucide-react";
import { FadeUp } from "../components/Reveal";
import { useMachines } from "../hooks/useMachines";
import { SITE } from "../data/site";

const SpecTable = ({ rows }) => (
  <table className="mt-3 w-full text-sm">
    <tbody>
      {rows.map(([label, value]) => (
        <tr key={label} className="border-b border-line last:border-0">
          <td className="py-2.5 pr-3 align-top font-mono text-[10px] uppercase tracking-[0.15em] text-soil">
            {label}
          </td>
          <td className="py-2.5 text-right font-semibold text-ink">{value}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const SpecTableFull = ({ columns, rows }) => (
  <div className="mt-4 overflow-x-auto" data-testid="machine-spec-table">
    <table className="w-full min-w-[520px] text-sm">
      <thead>
        <tr className="border-b-2 border-ink/15">
          {columns.map((col, ci) => (
            <th
              key={col}
              className={`py-3 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-soil ${
                ci === 0 ? "text-left" : "text-right"
              }`}
            >
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri} className="border-b border-line last:border-0">
            {row.map((cell, ci) => (
              <td
                key={ci}
                className={`py-3 ${
                  ci === 0 ? "font-bold text-ink" : "text-right font-semibold text-ink/80"
                }`}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function MachineDetail() {
  const { slug } = useParams();
  const { data: machines } = useMachines();
  const machine = machines.find((m) => m.slug === slug);
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const goTo = (hash) => (event) => {
    if (event) event.preventDefault();
    navigate(hash ? `/#${hash}` : "/", { state: hash ? { scroll: hash } : undefined });
  };

  useEffect(() => {
    setActive(0);
  }, [slug]);

  useEffect(() => {
    if (!machine) return;
    document.title = machine.seo.title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", machine.seo.description);
    return () => {
      document.title = "Baytaş Tarım | Tarım Makineleri";
      if (meta)
        meta.setAttribute(
          "content",
          "Baytaş Tarım tarım makineleri ve tarımsal çözümler. Makinelerimizi inceleyin ve detaylı bilgi için bizimle iletişime geçin."
        );
    };
  }, [machine]);

  if (!machine) {
    return (
      <main className="mx-auto max-w-7xl px-4 pb-24 pt-36 sm:px-8" data-testid="machine-not-found">
        <h1 className="font-display text-3xl font-bold uppercase tracking-tight text-ink">
          Makine bulunamadı
        </h1>
        <p className="mt-3 text-soil">Aradığınız makine listenizde yok olabilir.</p>
        <Link to="/" data-testid="machine-back-home" className="btn btn-dark mt-6">
          Ana Sayfaya Dön
        </Link>
      </main>
    );
  }

  return (
    <main className="bg-paper pb-24 pt-24" data-testid="machine-detail-page">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <button
          type="button"
          onClick={goTo("makineler")}
          data-testid="machine-back-btn"
          className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-soil transition-colors duration-200 hover:text-ink"
        >
          <ArrowLeft size={14} />
          Makinelerimiz
        </button>

        <div className="mt-6 grid gap-10 lg:grid-cols-2 lg:gap-14">
          <div>
            <FadeUp>
              <figure className="overflow-hidden rounded-lg border border-line bg-paper-deep">
                <img
                  src={machine.gallery[active]}
                  alt={`${machine.name} - Baytaş Tarım`}
                  className="aspect-[4/3] w-full object-contain"
                  data-testid="machine-detail-image"
                />
              </figure>
              <div className="mt-4 flex gap-3">
                {machine.gallery.map((src, i) => (
                  <button
                    key={src}
                    type="button"
                    onClick={() => setActive(i)}
                    data-testid={`machine-gallery-thumb-${i}`}
                    aria-label={`${machine.name} fotoğraf ${i + 1}`}
                    className={`overflow-hidden rounded-md border-2 transition-colors duration-200 ${
                      i === active ? "border-gold" : "border-line hover:border-ink/40"
                    }`}
                  >
                    <img src={src} alt="" className="h-20 w-28 object-cover" />
                  </button>
                ))}
              </div>
            </FadeUp>
          </div>

          <div>
            <FadeUp>
              <span className="inline-block bg-forest px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-paper">
                {machine.category}
              </span>
              <h1
                className="mt-4 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight text-ink sm:text-5xl"
                data-testid="machine-detail-title"
              >
                {machine.name}
              </h1>
              <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-gold">
                {machine.tagline}
              </p>
              <p className="mt-5 text-base leading-relaxed text-soil sm:text-lg" data-testid="machine-detail-description">
                {machine.description}
              </p>
            </FadeUp>

            <FadeUp delay={0.12}>
              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href="#iletisim"
                  onClick={goTo("iletisim")}
                  data-testid="machine-contact-cta"
                  className="btn btn-primary"
                >
                  Bize Ulaşın
                </a>
                <a href={SITE.phones[0].href} data-testid="machine-call-cta" className="btn btn-ghost">
                  <Phone size={16} />
                  Telefonla Ara
                </a>
              </div>
            </FadeUp>
          </div>
        </div>

        <FadeUp>
          <div className="card mt-14 p-6 sm:p-8" data-testid="machine-specs">
            <h2 className="font-display text-xl font-bold uppercase tracking-tight text-ink sm:text-2xl">
              Teknik Özellikler
            </h2>
            {machine.specTables ? (
              <div className="mt-6 space-y-10">
                {machine.specTables.map((table) => (
                  <div
                    key={table.title}
                    data-testid={`machine-spec-table-${table.title
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                  >
                    <h3 className="inline-block bg-forest px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-paper">
                      {table.title}
                    </h3>
                    <SpecTableFull columns={table.columns} rows={table.rows} />
                  </div>
                ))}
              </div>
            ) : machine.specTable ? (
              <SpecTableFull columns={machine.specTable.columns} rows={machine.specTable.rows} />
            ) : machine.specGroups ? (
              <div className="mt-6 grid gap-10 md:grid-cols-2">
                {machine.specGroups.map((group) => (
                  <div key={group.title} data-testid={`machine-spec-group-${group.title.toLowerCase()}`}>
                    <h3 className="inline-block bg-forest px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-paper">
                      {group.title}
                    </h3>
                    <SpecTable rows={group.rows} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-4 max-w-2xl">
                <SpecTable rows={machine.specs} />
              </div>
            )}
          </div>
        </FadeUp>

        <FadeUp>
          <div className="mt-14 flex flex-col items-start justify-between gap-6 rounded-lg bg-forest p-8 text-paper lg:flex-row lg:items-center lg:p-10" data-testid="machine-cta-band">
            <div>
              <h2 className="font-display text-2xl font-bold uppercase leading-tight tracking-tight sm:text-3xl">
                Bu makine hakkında bilgi almak ister misiniz?
              </h2>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-paper/75">
                Formu doldurun ya da doğrudan arayın; ekibimiz size uygun çözümü anlatmak için
                hazır.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <a
                href="#iletisim"
                onClick={goTo("iletisim")}
                data-testid="machine-cta-contact"
                className="btn btn-amber"
              >
                Bize Ulaşın
              </a>
              <a href={SITE.phones[0].href} data-testid="machine-cta-call" className="btn btn-ghost-light">
                <Phone size={16} />
                Telefonla Ara
              </a>
            </div>
          </div>
        </FadeUp>
      </div>
    </main>
  );
}